// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { MedplumClient } from '@medplum/core';
import { getReferenceString } from '@medplum/core';
import type { ChargeItem, ChargeItemDefinition, Encounter } from '@medplum/fhirtypes';
import {
  applyChargeItemDefinition as applySharedChargeItemDefinition,
  appointmentChargeDefinitionUrl,
} from '@polarissleep/shared';

export const CPT = 'http://www.ama-assn.org/go/cpt';

export async function findActiveChargeItemDefinitionByCpt(
  medplum: MedplumClient,
  cptCode: string | undefined
): Promise<ChargeItemDefinition | undefined> {
  if (!cptCode) {
    return undefined;
  }

  const results = await medplum.searchResources(
    'ChargeItemDefinition',
    new URLSearchParams({
      url: appointmentChargeDefinitionUrl(cptCode),
      status: 'active',
      _count: '1',
    })
  );

  return results[0];
}

/**
 * Standalone function to fetch and apply ChargeItemDefinition to charge item
 * @param medplum - Medplum client instance
 * @param chargeItem - Current charge item
 * @returns Promise with updated charge items
 */
export async function applyChargeItemDefinition(medplum: MedplumClient, chargeItem: ChargeItem): Promise<ChargeItem> {
  return applySharedChargeItemDefinition(medplum, chargeItem);
}

export async function getChargeItemsForEncounter(medplum: MedplumClient, encounter: Encounter): Promise<ChargeItem[]> {
  if (!encounter) {
    return [];
  }

  const chargeItems = await medplum.searchResources('ChargeItem', `context=${getReferenceString(encounter)}`);
  const updatedChargeItems = await Promise.all(
    chargeItems.map(async (chargeItem) => {
      // ChargeItems may represent historical payments. Their persisted price is
      // authoritative and must not be replaced by the current chargemaster.
      if (typeof chargeItem.priceOverride?.value === 'number') {
        return chargeItem;
      }

      try {
        return await applyChargeItemDefinition(medplum, chargeItem);
      } catch {
        // One malformed definition must not hide every other billing line.
        return chargeItem;
      }
    })
  );
  return updatedChargeItems;
}

export function calculateTotalPrice(items: ChargeItem[]): number {
  return items.reduce((sum, item) => sum + (item.priceOverride?.value || 0), 0);
}

export function formatMoney(value: number | undefined, currency?: string): string {
  if (value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  const code = currency ?? 'USD';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
}

export function getInvoiceNumber(chargeItem: ChargeItem): string {
  const idIdentifier = chargeItem.identifier?.find(
    (identifier) => identifier.system !== 'https://polarissleep.com/identifiers/patient-bill'
  )?.value;
  if (idIdentifier) {
    return idIdentifier;
  }
  return chargeItem.id ? `POL-${chargeItem.id.slice(0, 8).toUpperCase()}` : '—';
}
